// modules
import { div } from 'exact-math';
import Fetch from 'modules/Fetch';
import AppService from 'modules/App';
import Localization from 'modules/Localization';
import ValidationService from 'modules/Validation';
import { CleverTapService } from 'modules/CleverTap';
import { FCMNotificationConfig } from 'modules/Firebase/cloudMessaging/typings';
import { PriceAlertNotification } from 'modules/PriceAlerts/typings';
import Notifications from 'modules/Notifications';
import ErrorService from 'modules/Error';
import AnalyticsService from 'modules/Analytics/service';
import Search from 'modules/Search';
import LoadingService from 'modules/Loading';
// constant
import { EVENTS } from 'modules/Analytics/consts';
// store
import PriceAlertsStore from './store';
// typings
import {
  AlertPayload,
  GeneratePriceAlertPayload,
  GetPriceAlertResponse,
  PostAlertResponse,
  PriceAlertType,
} from './typings';

class PriceAlertsService extends PriceAlertsStore {
  private priceAlertGenericError = (error?: Error) => {
    Notifications.pushToast('PRICE_ALERT', {
      title: Localization.translate(error ? 'PRICE_ALERTS' : 'DEFAULT_ERROR_MESSAGE'),
      text: error?.message || Localization.translate('DEFAULT_ERROR_HELP_TEXT'),
      type: 'error',
    });
  }

  getPriceAlerts = async () => {
    try {
      ErrorService.clearError('PRICE_ALERT');
      const response = await Fetch.request<'PRICE_ALERT', GetPriceAlertResponse>(
        'PRICE_ALERT',
        '/user',
        'PRICE_ALERT',
        {
          method: 'GET',
        },
      );
      if (response.success) {
        const { data } = response;
        this.setPriceAlertsList(data.priceAlerts);
      }

      if (response.error) {
        AnalyticsService.sendEvent(EVENTS.PRICE_ALERTS_ERROR, {
          error: response.error,
        }, true);
      }
    } catch (err) {
      this.priceAlertGenericError();
      AnalyticsService.sendEvent(EVENTS.PRICE_ALERTS_ERROR, {
        err,
      }, true);
    }
  };

  deletePriceAlert = async (
    alertId: string,
    exchangeToken: string,
    showNotification = true,
  ) => {
    let scripInfo;
    try {
      if (exchangeToken) {
        scripInfo = await Search.searchByExchangeToken(exchangeToken);
      }
      const response = await Fetch.request<'PRICE_ALERT', GetPriceAlertResponse>(
        'PRICE_ALERT',
        '/user',
        'PRICE_ALERT_DELETE',
        {
          method: 'DELETE',
          pathParameter: alertId,
        },
      );
      if (response?.success && scripInfo?.displayName && showNotification) {
        Notifications.pushToast('PRICE_ALERT_DELETE', {
          title: Localization.translate('ALERT_ON'),
          text: Localization.translate('ALERT_ON_TEXT_DELETE', scripInfo?.name),
          type: 'success',
        });
      }
      if (response.success) {
        return response;
      }
      if (response.error) {
        this.priceAlertGenericError(new Error(response.error.message));
        AnalyticsService.sendEvent(EVENTS.PRICE_ALERTS_ERROR, {
          error: response.error,
        }, true);
      }
    } catch (err) {
      this.priceAlertGenericError();
      AnalyticsService.sendEvent(EVENTS.PRICE_ALERTS_ERROR, {
        err,
      }, true);
    } finally {
      if (scripInfo) {
        AnalyticsService.sendEvent(EVENTS.PRICE_ALERT_DELETE_BUTTON_CLICKED, {
          Symbol: scripInfo.underlyingSymbol || scripInfo.symbol,
          Scrip: scripInfo.name,
          'Instrument Type': scripInfo.instrument,
        });
      }
    }
  };

  private isRulesValid = (
    alertPayload: AlertPayload,
    ltp?: number,
    validationError?: 'notification',
  ): boolean => {
    let errorMessage = '';
    const priceAboveRule = alertPayload.rules.find(rule => rule.type === 'ABOVE');
    const priceBelowRule = alertPayload.rules.find(rule => rule.type === 'BELOW');

    const priceAbove = priceAboveRule ? priceAboveRule.value : NaN;
    const priceBelow = priceBelowRule ? priceBelowRule.value : NaN;

    const exchange = this.scripData?.segment;

    const digitsAfterDot = exchange &&
      ValidationService.isCurrency(exchange) ? 4 : 2;
    const tickSize = this.scripData?.tickSize ||
      (digitsAfterDot === 4 ? 0.01 : 1);
    const tickSizeDiv = div(tickSize, 100);

    // Inline validation logic
    const validations = [
      { type: 'ABOVE', price: priceAbove, setError: this.setPriceAboveError },
      { type: 'BELOW', price: priceBelow, setError: this.setPriceBelowError },
    ];

    const isValid = validations.map(({ price, setError, type }) => {
      let valid = true;

      if (type === 'ABOVE' && Number(ltp) >= priceAbove) {
        errorMessage = Localization.translate('PRICE_BELOW_LTP_ERROR');
        setError(errorMessage);
        valid = false;
      }
      if (type === 'BELOW' && Number(ltp) <= priceBelow) {
        errorMessage = Localization.translate('PRICE_ABOVE_LTP_ERROR');
        setError(errorMessage);
        valid = false;
      }

      if (price <= 0) {
        errorMessage = Localization.translate('LESS_THAN_ZERO_ERROR');
        setError(errorMessage);
        valid = false;
      }

      if (price > 0 && !ValidationService.isMultiple(price, tickSizeDiv)) {
        errorMessage = Localization.translate('MULTIPLE_OF', `${tickSizeDiv}`);
        setError(errorMessage);
        valid = false;
      }

      if (valid) {
        setError('');
      }
      return valid;
    }).every(valid => valid);

    if (!isValid && validationError === 'notification') {
      this.setPriceAboveError('');
      this.setPriceBelowError('');
      this.priceAlertGenericError(new Error(errorMessage));
    }
    return isValid;
  };

  private generatePriceAlertPayload = (type: GeneratePriceAlertPayload)
  : AlertPayload => {
    const rules = [];

    if (this.priceAboveValue) {
      const ruleType: PriceAlertType = 'ABOVE';
      rules.push({
        type: ruleType,
        value: Number(this.priceAboveValue),
        notes: this.priceAboveNotes,
      });
    }

    if (this.priceBelowValue) {
      const ruleType: PriceAlertType = 'BELOW';
      rules.push({
        type: ruleType,
        value: Number(this.priceBelowValue),
        notes: this.priceBelowNotes,
      });
    }

    if (type === 'CREATE') {
      return {
        instrumentKey: this.scripData?.instrumentKey || '',
        compareWith: 'LTP',
        rules,
      };
    }

    return {
      compareWith: 'LTP',
      rules,
    };
  };

  // Pure function for making the API call for create price alert
  createPriceAlert = async (
    payload: AlertPayload,
    exchangeToken?: string,
    ltp?: number,
  ) => {
    if (!this.isRulesValid(payload, ltp)) return;
    let scripInfo: DataSchema | null = null;
    try {
      if (exchangeToken) {
        scripInfo = await Search.searchByExchangeToken(exchangeToken);
      }
      const response = await Fetch.request<'PRICE_ALERT', PostAlertResponse>(
        'PRICE_ALERT',
        '/user',
        'CREATE_PRICE_ALERT',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            data: payload,
          },
        },
      );
      if (response.error) {
        this.priceAlertGenericError(new Error(response.error.message));
        AnalyticsService.sendEvent(EVENTS.PRICE_ALERTS_ERROR, {
          error: response.error,
        }, true);
      }
      if (response?.success && scripInfo?.name) {
        Notifications.deleteToast('PRICE_ALERT_CREATE');
        const { id } = response.data;
        Notifications.pushToast('PRICE_ALERT', {
          title: Localization.translate('ALERT_ON'),
          text: Localization.translate('ALERT_ON_TEXT', scripInfo?.name),
          type: 'success',
          action: {
            type: 'button',
            btnText: Localization.translate('EDIT_ALERT'),
            actionCallback: () => {
              if (scripInfo) {
                this.setScripData(scripInfo);
              }
              this.actionHandlerOnCreate(id, payload);
            },
          },
        });
      }
      return response;
    } catch (error) {
      AnalyticsService.sendEvent(EVENTS.PRICE_ALERTS_ERROR, {
        error,
      }, true);
    } finally {
      if (scripInfo) {
        const aboveRule = payload.rules.find(item => item.type === 'ABOVE');
        const belowRule = payload.rules.find(item => item.type === 'BELOW');

        const getRuleCheck = (key: 'type' | 'notes') => {
          if (aboveRule?.[key] && belowRule?.[key]) return 'BOTH';
          if (aboveRule?.[key]) return 'ABOVE';
          if (belowRule?.[key]) return 'BELOW';
          return '';
        };
        const priceCheck = getRuleCheck('type');
        const noteCheck = getRuleCheck('notes');

        AnalyticsService.sendEvent(EVENTS.PRICE_ALERT_CREATE_BUTTON_CLICK, {
          Symbol: scripInfo.underlyingSymbol || scripInfo.symbol,
          Scrip: scripInfo.name,
          Segment: scripInfo.segment,
          Status: 'Active',
          'Price Check': priceCheck,
          'Instrument Type': scripInfo.instrument,
          'Add Notes': noteCheck,
        });
      }
    }
  };

  private actionHandlerOnCreate = (
    id: string,
    alertPayload: AlertPayload,
  ) => {
    Notifications.deleteToast('PRICE_ALERT');
    this.setCurrentAlertId(id);
    const priceBelow = alertPayload.rules.find(rule => rule.type === 'BELOW');
    const priceAbove = alertPayload.rules.find(rule => rule.type === 'ABOVE');
    if (priceBelow) {
      this.setPriceBelow(priceBelow.value.toString());
      this.setPriceBelowNotes(priceBelow.notes);
    }
    if (priceAbove) {
      this.setPriceAbove(priceAbove.value.toString());
      this.setPriceAboveNotes(priceAbove.notes);
    }
    this.setModalType('Create');
    AppService.setModal('priceAlerts', true);
  }

  // Method to handle the creation process
  createAlert = async (exchangeToken?: string, ltp?: number) => {
    const alertPayload = this.generatePriceAlertPayload('CREATE');
    const response = await this.createPriceAlert(
      alertPayload, exchangeToken, ltp);
    CleverTapService.requestPermission();
    return response;
  };

  // Pure function for making the API call
  updatePriceAlert = async (
    alertId: string,
    payload: AlertPayload,
    exchangeToken?: string,
    ltp?: number,
    validationError?: 'notification',
  ) => {
    if (!this.isRulesValid(payload, ltp, validationError)) return;
    let scripInfo: DataSchema | null = null;
    try {
      if (exchangeToken) {
        scripInfo = await Search.searchByExchangeToken(exchangeToken);
      }
      const response = await Fetch.request<'PRICE_ALERT', PostAlertResponse>(
        'PRICE_ALERT',
        '/user',
        'EDIT_PRICE_ALERT',
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            data: payload,
          },
          pathParameter: alertId,
        },
      );
      if (response?.error) {
        this.priceAlertGenericError(new Error(response.error.message));
        AnalyticsService.sendEvent(EVENTS.PRICE_ALERTS_ERROR, {
          error: response.error,
        }, true);
      }
      if (response?.success && scripInfo?.name) {
        Notifications.deleteToast('PRICE_ALERT_UPDATE');
        const { id } = response.data;
        Notifications.pushToast('PRICE_ALERT_UPDATE', {
          title: Localization.translate('ALERT_ON'),
          text: Localization.translate('ALERT_ON_UPDATE_TEXT', scripInfo?.name),
          type: 'success',
          action: {
            type: 'button',
            btnText: Localization.translate('EDIT_ALERT'),
            actionCallback: () => {
              if (scripInfo) {
                this.setScripData(scripInfo);
              }
              this.actionHandlerOnCreate(id, payload);
            },
          },
        });
      }
      return response;
    } catch (err) {
      this.priceAlertGenericError();
      AnalyticsService.sendEvent(EVENTS.PRICE_ALERTS_ERROR, {
        err,
      }, true);
    } finally {
      if (scripInfo) {
        const aboveRule = payload.rules.find(item => item.type === 'ABOVE');
        const belowRule = payload.rules.find(item => item.type === 'BELOW');

        const getRuleCheck = (key: 'type' | 'notes') => {
          if (aboveRule?.[key] && belowRule?.[key]) return 'BOTH';
          if (aboveRule?.[key]) return 'ABOVE';
          if (belowRule?.[key]) return 'BELOW';
          return '';
        };
        const priceCheck = getRuleCheck('type');

        AnalyticsService.sendEvent(EVENTS.PRICE_ALERT_EDIT_BUTTON_CLICKED, {
          Symbol: scripInfo.underlyingSymbol || scripInfo.symbol,
          Scrip: scripInfo.name,
          Segment: scripInfo.segment,
          'Updated Price Check': priceCheck,
          'Instrument Type': scripInfo.instrument,
        });
      }
    }
  };

  editAlert = async (exchangeToken?: string, ltp?: number) => {
    const alertPayload = this.generatePriceAlertPayload('EDIT');
    const response = await this.updatePriceAlert(
      this.currentAlertId,
      alertPayload,
      exchangeToken,
      ltp,
    );
    CleverTapService.requestPermission();
    return response;
  };

  fetchPriceAlertsIfNotLoading = () => {
    const isLoading = LoadingService.getLoading('PRICE_ALERT');
    if (isLoading) return;
    this.getPriceAlerts();
  };

  handlePriceAlert = async (payload: FCMNotificationConfig) => {
    if (!payload.priceConfig) return;
    try {
      const priceConfig: PriceAlertNotification =
        JSON.parse(payload.priceConfig);
      if (!priceConfig) return;
      priceConfig.id = payload.id;
      this.showNotification(priceConfig);
      this.fetchPriceAlertsIfNotLoading();
      let scripInfo: DataSchema | null = null;
      const exchangeToken = `${priceConfig.e}|${priceConfig.t}`;
      if (exchangeToken) {
        scripInfo = await Search.searchByExchangeToken(exchangeToken);
      }
      AnalyticsService.sendEvent(EVENTS.PRICE_ALERT_TRIGGERED, {
        Symbol: scripInfo?.underlyingSymbol || scripInfo?.symbol,
        Scrip: scripInfo?.displayName,
        Segment: scripInfo?.segment,
        'Instrument Type': scripInfo?.instrument,
      });
    } catch (err) {
      AnalyticsService.sendEvent(EVENTS.PRICE_ALERTS_ERROR, {
        err,
      }, true);
    }
  };
}

export default new PriceAlertsService();
